import {Component, Input} from '@angular/core';
import {Group} from "../../model/group";
import {ModalsService} from "../../services/modals.service";
import {Rule} from "../../model/rule";
import {Contact} from "../../model/contact";
import {catchError, of} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {RulesService} from "../../services/rules.service";
import {constants} from "../../constants";
import {MetrikaService} from "../../services/metrika.service";

@Component({
  selector: 'app-edit-group-modal',
  templateUrl: './edit-group-modal.component.html',
  styleUrls: ['./edit-group-modal.component.css']
})
export class EditGroupModalComponent {
  private _group: Group;
  newGroup: boolean;
  showRules: boolean;
  rulesSaved: boolean;
  requestInProgress: boolean;

  title: string;
  contacts: Array<Contact>;
  contactsError: string;
  groupError: string;
  rules: Rule[];

  nextActive: boolean;

  titlePattern = constants.titlePattern;

  constructor(private readonly modalsService: ModalsService,
              private readonly rulesService: RulesService,
              private readonly metrikaService: MetrikaService) {
    this._group = null;
    this.newGroup = false;
    this.showRules = false;
    this.rulesSaved = false;
    this.requestInProgress = false;

    this.title = '';
    this.contacts = this.contacts = this.filterGroupNumbers(rulesService.contactsValue);
    this.contactsError = null;
    this.groupError = null;
    this.rules = [];

    this.rulesService.contactsValue.forEach(c => c.selected = null);

    this.nextActive = false;
  }

  get group(): Group {
    return this._group;
  }

  @Input() set group(value: Group) {
    this._group = value;
    this.newGroup = !value;

    if (value) {
      this.title = value.title;
      const filteredContacts = this.filterGroupNumbers(this.rulesService.contactsValue);
      this.contacts = value.numbers.concat(
        filteredContacts.filter(contact => !value.numbers.includes(contact))
      );
      this.rules = value.rules;

      value.numbers.forEach(c => c.selected = true);
    }

    this.groupFieldChanged();
  }

  close(): void {
    this.modalsService.hideModal();
  }

  back(): void {
    if (this.showRules) {
      this.showRules = false;
    } else {
      this.modalsService.showGroupInfo(this._group);
    }
  }

  groupFieldChanged(): void {
    this.nextActive = this.title?.trim().length > 0 && this.contacts.filter(c => c.selected).length > 0;
  }

  groupChange(): void {
    this.groupError = null
  }

  contactsSelectionChanged(): void {
    this.contactsError = null;
    this.groupFieldChanged();
  }

  saveGroup(): void {
    if (this.nextActive && !this.requestInProgress) {
      this.requestInProgress = true;

      let groupContacts = this.contacts.filter(c => c.selected);

      if (this.newGroup) {
        this.rulesService.createGroup(new Group(null, this.title.trim(), groupContacts))
          .pipe(catchError((error: HttpErrorResponse) => {
            if (error.status == 404) {
              this.contactsError = 'edit-group-modal.contact-not-found';
            } else if (error.status == 409) {
              this.contactsError = 'edit-group-modal.contact-used-in-group';
            } else if (error.status == 422) {
              this.groupError = 'edit-group-modal.group-already-exists'
            } else {
              this.contactsError = 'edit-group-modal.unexpected-error';
            }
            return of(null);
          }))
          .subscribe(id => {
            if (id) {
              this.group = new Group(id, this.title.trim(), groupContacts);
              this.showRules = true;
                this.metrikaService.addEvent('create_group', null, 'SUCCESS')
            }
            this.requestInProgress = false;
          });
      } else {
        this.rulesService.editGroup(new Group(this.group.id, this.title.trim(), groupContacts))
          .subscribe(status => {
            if (status == 200) {
              this.group.title = this.title.trim();
              this.group.numbers = groupContacts;
              this.metrikaService.addEvent('edit_group', null, 'SUCCESS')
              this.showRules = true;
            } else if (status == 404) {
              this.contactsError = 'edit-group-modal.contact-not-found';
            } else if (status == 409) {
              this.contactsError = 'edit-group-modal.contact-used-in-group';
            } else if (status == 422) {
              this.groupError = 'edit-group-modal.group-already-exists';
            } else {
              this.contactsError = 'edit-group-modal.unexpected-error';
            }
            this.requestInProgress = false;
          });
      }
    }
  }

  saveRules(rulesToSave: Array<Rule>): void {
    if (!this.requestInProgress) {
      this.requestInProgress = true;
      this.rulesService.updateGroupRules(this.group.id, rulesToSave)
        .subscribe(code => {
          if (code == 200) {
            this.rulesSaved = true;
            this.rulesService.loadContacts();
            this.rulesService.loadGroups();
          } else {
            console.error(`Cannot save group rules, code: ${code}`);
          }
          this.requestInProgress = false;
        });
    }
  }

  deleteRules() {
    if (!this.requestInProgress) {
      this.requestInProgress = true;
      this.rulesService.deleteGroupRules(this.group.id)
        .subscribe(code => {
          if (code == 200) {
            this.rulesSaved = true;
            this.rulesService.loadContacts();
            this.rulesService.loadGroups();
          } else {
            console.error(`Cannot save contact rules, code: ${code}`);
          }
          this.requestInProgress = false;
        });
    }
  }

  private filterGroupNumbers(numbers: Array<Contact>): Array<Contact> {
    if (!numbers || numbers.length === 0) {
      return [];
    }

    const bestContacts: { [msisdn: string]: Contact } = {};

    for (const contact of numbers) {
      const msisdn = contact.msisdn;

      if (!bestContacts[msisdn]) {
        bestContacts[msisdn] = contact;
      } else {
        const existing = bestContacts[msisdn];
        const existingIsGood = existing.title !== existing.msisdn;
        const newIsGood = contact.title !== contact.msisdn;

        if (newIsGood && !existingIsGood) {
          bestContacts[msisdn] = contact;
        }
      }
    }

    let result = Object.values(bestContacts);

    result.sort((a, b) => {
      const aIsGood = a.title !== a.msisdn;
      const bIsGood = b.title !== b.msisdn;

      if (aIsGood !== bIsGood) {
        return aIsGood ? -1 : 1;
      }

      const nameA = aIsGood ? a.title : a.msisdn;
      const nameB = bIsGood ? b.title : b.msisdn;
      return nameA.localeCompare(nameB);
    });

    return result;
  }
}
